import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { useDashboard } from '../store/useDashboard';
import api from '../services/api';
import MenuProfil from '../components/MenuProfil';
import BandeauDelegation from '../components/BandeauDelegation';
import {
  IconeListe, IconeNote, IconeAvion, IconeRecu, IconeRafraichir, IconeDocument,
  IconeCouronne, IconeHorloge, IconeValide, IconeRefuse, IconeCrayon, IconeTrombone,
  IconePlume, IconeAlerte, IconeDossier,
} from '../components/IconesSVG';

const TYPES_DOCS = {
  expression_besoin: { label: 'Fiche EB',        icon: IconeListe, color: '#2980B9' },
  bon_commande:      { label: 'Bon commande',     icon: IconeNote, color: '#8E44AD' },
  frais_mission:     { label: 'Frais mission',    icon: IconeAvion, color: '#E67E22' },
  facture:           { label: 'Facture',          icon: IconeRecu, color: '#16A085' },
  regularisation:    { label: 'Regularisation',   icon: IconeRafraichir, color: '#935116' },
};

const getToken = () => {
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      const val = localStorage.getItem(key);
      if (val?.startsWith('eyJ')) return val;
      try { const p = JSON.parse(val); return p?.state?.token || p?.token; } catch { }
    }
  } catch { }
  return null;
};

export default function ParapheurPage() {
  const navigate = useNavigate();
  const { allerDashboard } = useDashboard();
  const { user } = useAuthStore();

  const [pochettes, setPochettes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [onglet, setOnglet] = useState(0);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPDF, setLoadingPDF] = useState(false);

  // Signature PDG : apercu du memo Holding scelle, rendu par le serveur
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [infoSignataire, setInfoSignataire] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [scellageTermine, setScellageTermine] = useState(false);
  const pdfViewerRef = useRef(null);
  const chargementPdfRef = useRef(0); // invalide les chargements PDF obsoletes

  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionMode, setActionMode] = useState(null);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      setLoading(true);
      const res = await api.get('/demandes?statut=dans_parapheur_pdg');
      setPochettes(res.data?.data || res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const ouvrirPochette = async (p) => {
    setSelected(p);
    setOnglet(0);
    setPdfUrl(null);
    reinitialiserPreview();
    setScellageTermine(false); // si un autre dossier est ouvert pendant l'ecran de confirmation
    setError('');
    setSuccess('');
    setActionMode(null);
    setCommentaire('');
    const docs = p.documents || [];
    if (docs.length > 0) chargerPDF(docs[0]);
  };

  const chargerPDF = async (doc) => {
    // Chaque chargement porte un numero : si un autre onglet est demande avant la
    // fin du fetch, le blob obsolete n'atteindra jamais l'etat et doit etre libere
    // sur place (le useEffect ci-dessous ne libere que ce qui a ete affiche).
    const generation = ++chargementPdfRef.current;
    setLoadingPDF(true);
    setPdfUrl(null);
    try {
      const token = getToken();
      const response = await fetch(( process.env.REACT_APP_STATIC_URL || 'http://localhost:5001') + '/' + doc.chemin, {
        headers: token ? { 'Authorization': 'Bearer ' + token } : {}
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      if (generation !== chargementPdfRef.current) { URL.revokeObjectURL(url); return; }
      setPdfUrl(url);
    } catch { }
    finally { if (generation === chargementPdfRef.current) setLoadingPDF(false); }
  };

  // Le PDF arrive en base64 : passer par un Uint8Array, sinon les octets > 127
  // seraient reencodes en UTF-8 par le Blob et le fichier serait corrompu.
  const base64VersBlobUrl = (base64) => {
    const binaire = atob(base64);
    const octets = new Uint8Array(binaire.length);
    for (let i = 0; i < binaire.length; i++) octets[i] = binaire.charCodeAt(i);
    return URL.createObjectURL(new Blob([octets], { type: 'application/pdf' }));
  };

  // Libere l'apercu scelle et revient au document d'origine
  const reinitialiserPreview = () => {
    setPreviewBlobUrl(null);
    setInfoSignataire(null);
  };

  const changerOnglet = (idx) => {
    setOnglet(idx);
    const docs = selected?.documents || [];
    if (docs[idx]) chargerPDF(docs[idx]);
  };

  // Liberer l'apercu scelle : remplacement, retour au document d'origine, demontage
  useEffect(() => {
    if (!previewBlobUrl) return undefined;
    return () => URL.revokeObjectURL(previewBlobUrl);
  }, [previewBlobUrl]);

  // Liberer le PDF affiche : changement d'onglet, changement de dossier, demontage
  useEffect(() => {
    if (!pdfUrl) return undefined;
    return () => URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  // Au demontage, invalider tout chargement en cours : son blob se liberera seul
  useEffect(() => () => { chargementPdfRef.current = -1; }, []);

  // Fermeture du dossier (les setSelected(null) differes) : le viewer disparait,
  // plus rien ne doit retenir les blobs
  useEffect(() => {
    if (selected) return;
    setPdfUrl(null);
    setPreviewBlobUrl(null);
    setInfoSignataire(null);
    setScellageTermine(false);
  }, [selected]);

  // ===== SIGNATURE PDG =====
  // Le serveur regenere le memo Holding avec la signature enregistree sur le compte
  // et renvoie un apercu. Rien n'est persiste tant que le PDG n'a pas confirme.
  const chargerSignatureScellee = async () => {
    setLoadingPreview(true); setError('');
    try {
      // L'intercepteur axios renvoie deja response.data : le payload est a plat.
      const res = await api.get('/demandes/' + selected.id + '/preview-signature');
      if (res?.success) {
        setInfoSignataire(res.signataire);
        setPreviewBlobUrl(base64VersBlobUrl(res.pdf_base64));
        // Se placer sur l'onglet du memo Holding
        const docs = selected?.documents || [];
        const memoIdx = docs.findIndex(d => d.categorie === 'memo_holding');
        if (memoIdx >= 0) changerOnglet(memoIdx);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Erreur chargement signature');
    } finally {
      setLoadingPreview(false);
    }
  };

  const confirmerSignaturePDG = async () => {
    if (!previewBlobUrl) return;
    setSaving(true); setError('');
    try {
      // Le PDF scelle est regenere et persiste cote serveur par la route visa :
      // inutile de renvoyer l'apercu, la route repart des donnees_memo du document.
      await api.post('/demandes/' + selected.id + '/visa', {
        type_visa: 'approbation',
        commentaire: commentaire || 'Signe et scelle par le PDG Groupe',
      });
      // Ne pas relacher l'apercu ici : le viewer retomberait sur pdfUrl, c'est-a-dire
      // le memo NON signe, le temps que le dossier se ferme. L'ecran de confirmation
      // le recouvre jusqu'au setSelected(null), qui libere tout.
      setScellageTermine(true);
      setSuccess('Dossier signé et scellé ! Archivé définitivement.');
      setCommentaire('');
      charger();
      setTimeout(() => { setSelected(null); setSuccess(''); }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally { setSaving(false); }
  };

  const rejeter = async () => {
    if (!commentaire.trim()) { setError('Motif obligatoire'); return; }
    setSaving(true);
    try {
      await api.post('/demandes/' + selected.id + '/visa', {
        type_visa: 'rejet',
        commentaire,
      });
      setSuccess('Dossier rejeté');
      charger();
      setTimeout(() => { setSelected(null); setSuccess(''); }, 2000);
    } catch (err) { setError(err.response?.data?.message || err.message); }
    finally { setSaving(false); }
  };

  const formatMontant = (m) => m ? Number(m).toLocaleString('fr-FR') + ' F' : '—';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  const pCount = pochettes.length;
  const docs = selected?.documents || [];
  const memoHolding = docs.find(d => d.categorie === 'memo_holding');
  const memoFiliale = docs.find(d => d.categorie === 'memo_filiale');

  return (
    <div style={s.page}>
      {/* TOPBAR */}
      <div style={s.topbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/logo.png" alt="JETA" style={{ height: 28 }} onError={e => { e.target.style.display = 'none'; }} />
          <div onClick={allerDashboard} title="Retour au tableau de bord" style={{ cursor: 'pointer' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Parapheur Présidentiel</div>
            <div style={{ fontSize: 11, color: '#8899AA' }}>{user?.prenom} {user?.nom} · PDG Groupe</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {pCount > 0 && (
            <div style={{ background: '#C0392B', borderRadius: 20, padding: '6px 16px', color: '#fff', fontSize: 13, fontWeight: 700 }}>
              <IconeCouronne taille={14} /> {pCount} dossier{pCount > 1 ? 's' : ''} en attente de signature
            </div>
          )}
          <MenuProfil couleurAvatar="#6C3483" />
        </div>
      </div>

      <div style={s.main}>
        {/* LISTE */}
        <div style={s.liste}>
          <div style={s.listeTitre}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Dossiers à signer</span>
            <span style={{ fontSize: 11, background: '#F0F0F0', padding: '2px 8px', borderRadius: 10 }}>{pCount}</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={s.emptyState}><IconeHorloge taille={14} /> Chargement...</div>
            ) : pCount === 0 ? (
              <div style={s.emptyState}>
                <div style={{ marginBottom: 10, color: '#1E8449' }}><IconeValide taille={40} /></div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0D1B2A', marginBottom: 4 }}>Aucun dossier</div>
                <div style={{ fontSize: 12, color: '#888' }}>Tous les dossiers sont traités</div>
              </div>
            ) : pochettes.map(p => {
              const typeInfo = TYPES_DOCS[p.type_document] || { icon: IconeDocument, color: '#666' };
              const isSelected = selected?.id === p.id;
              const prioBg = p.priorite === 'P1' ? '#FDEDEC' : p.priorite === 'P3' ? '#EAF4FB' : '#FEF9E7';
              const prioColor = p.priorite === 'P1' ? '#C0392B' : p.priorite === 'P3' ? '#2980B9' : '#9A7D0A';
              const hasMemoHolding = (p.documents || []).some(d => d.categorie === 'memo_holding');
              return (
                <div key={p.id} onClick={() => ouvrirPochette(p)}
                  style={{ ...s.listeItem, ...(isSelected ? { background: '#FDEDEC', borderLeft: '3px solid #C0392B' } : { borderLeft: '3px solid transparent' }) }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'inline-flex', color: typeInfo.color }}><typeInfo.icon taille={18} /></span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: isSelected ? '#C0392B' : '#0D1B2A' }}>{p.reference}</div>
                        <div style={{ fontSize: 10, color: '#888' }}>{formatDate(p.created_at)}</div>
                      </div>
                    </div>
                    <span style={{ fontSize: 10, background: prioBg, color: prioColor, padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                      {p.priorite || 'P2'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#555', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.titre || p.motif || '—'}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <span style={{ fontSize: 11, background: '#0D1B2A', color: '#fff', padding: '1px 8px', borderRadius: 6, fontWeight: 700 }}>
                        {p.filiale?.code || '—'}
                      </span>
                      {hasMemoHolding && <span style={{ fontSize: 10, background: '#EAFAF1', color: '#1E8449', padding: '1px 6px', borderRadius: 6, fontWeight: 600 }}><IconeValide taille={10} /> Mémo</span>}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#C0392B' }}>{formatMontant(p.montant)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SPLIT SCREEN */}
        {!selected ? (
          <div style={s.splitVide}>
            <div style={{ opacity: 0.15, marginBottom: 16 }}><IconeCouronne taille={64} /></div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>Parapheur Présidentiel</div>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>Sélectionnez un dossier pour le signer</div>
            <div style={{ fontSize: 12, color: '#aaa' }}>Tous les dossiers ont été validés par la Holding</div>
          </div>
        ) : (
          <div style={s.split}>
            {/* PDF GAUCHE */}
            <div style={s.splitLeft}>
              {/* Onglets */}
              <div style={s.onglets}>
                {docs.map((doc, i) => (
                  <button key={i} onClick={() => changerOnglet(i)}
                    style={{ ...s.onglet, ...(onglet === i ? s.ongletActif : {}) }}>
                    {doc.categorie === 'document_principal' ? <><IconeListe taille={13} /> Demande terrain</> :
                     doc.categorie === 'memo_filiale' ? <><IconeCrayon taille={13} /> Mémo Filiale (DG)</> :
                     doc.categorie === 'memo_holding' ? <><IconeDocument taille={13} /> Mémo Holding</> :
                     <><IconeTrombone taille={13} /> {(doc.nom || '').substring(0, 16)}</>}
                  </button>
                ))}
                {previewBlobUrl && !scellageTermine && (
                  <div style={{ marginLeft: 'auto', padding: '6px 14px', background: '#27AE60', fontSize: 11, color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <IconeCouronne taille={13} /> Aperçu scellé — non encore transmis
                  </div>
                )}
              </div>

              {/* PDF — l'apercu scelle prime sur le document d'origine */}
              <div ref={pdfViewerRef} style={s.pdfZone}>
                {scellageTermine ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#F7FDF9', textAlign: 'center', padding: 24 }}>
                    <div style={{ marginBottom: 16, color: '#ccc' }}><IconeCouronne taille={64} /></div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#1E8449', marginBottom: 8 }}>Dossier scellé</div>
                    <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                      Le mémo Holding signé a été enregistré et le dossier archivé définitivement.
                    </div>
                    <div style={{ fontSize: 12, color: '#aaa' }}>Retour au parapheur…</div>
                  </div>
                ) : loadingPreview ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#888', gap: 10 }}>
                    <div style={{ color: '#bbb' }}><IconeHorloge taille={40} /></div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2A' }}>Apposition de votre signature…</div>
                    <div style={{ fontSize: 12 }}>Génération de l'aperçu du mémo Holding</div>
                  </div>
                ) : loadingPDF && !previewBlobUrl ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}><IconeHorloge taille={14} /> Chargement...</div>
                ) : (previewBlobUrl || pdfUrl) ? (
                  <iframe src={previewBlobUrl || pdfUrl} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                    title={previewBlobUrl ? 'Apercu scelle' : 'Document'} />
                ) : (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: 12, opacity: 0.3 }}><IconeDocument taille={48} /></div>
                      <div>Aucun document</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* BLOC DROITE */}
            <div style={s.splitRight}>
              {/* Header */}
              <div style={s.decisionHeader}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{selected.reference}</div>
                    <div style={{ fontSize: 12, color: '#8899AA', marginTop: 4 }}>{selected.filiale?.code} · {selected.filiale?.nom}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>{selected.montant ? Number(selected.montant).toLocaleString('fr-FR') : '—'}</div>
                    <div style={{ fontSize: 10, color: '#8899AA' }}>FCFA</div>
                  </div>
                </div>
              </div>

              <div style={s.decisionBody}>
                {/* Résumé */}
                <div style={s.resumeGrid}>
                  {[
                    { label: 'Demandeur', value: (selected.demandeur?.prenom || '') + ' ' + (selected.demandeur?.nom || '') },
                    { label: 'Type', value: TYPES_DOCS[selected.type_document]?.label || selected.type_document },
                    { label: 'Priorite', value: selected.priorite || 'P2' },
                    { label: 'Documents', value: docs.length + ' fichier(s)' },
                  ].map((item, i) => (
                    <div key={i} style={s.resumeItem}>
                      <div style={s.resumeLabel}>{item.label}</div>
                      <div style={s.resumeValue}>{item.value || '—'}</div>
                    </div>
                  ))}
                </div>

                {/* Vérification documents */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#0D1B2A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Pièces du dossier
                  </div>
                  {[
                    { label: 'Demande terrain', present: docs.some(d => d.categorie === 'document_principal'), icon: IconeListe },
                    { label: 'Mémo Filiale signé DG', present: !!memoFiliale, icon: IconeCrayon },
                    { label: 'Mémo Holding', present: !!memoHolding, icon: IconeDocument },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '0.5px solid #f0f0f0', fontSize: 12 }}>
                      <span style={{ display: 'inline-flex', color: item.present ? '#1E8449' : '#96281B' }}>{item.present ? <IconeValide taille={14} /> : <IconeRefuse taille={14} />}</span>
                      <span style={{ color: item.present ? '#0D1B2A' : '#C0392B', fontWeight: item.present ? 500 : 600 }}>
                        <item.icon taille={12} /> {item.label}
                      </span>
                    </div>
                  ))}
                </div>

                {success && <div style={s.successMsg}>{success}</div>}
                {error && <div style={s.errorMsg}>{error}</div>}

                {/* ===== SIGNATURE PDG ===== */}
                {!previewBlobUrl && !actionMode && !scellageTermine && (
                  <div style={s.actionSection}>
                    <BandeauDelegation />
                    <div style={s.actionTitle}><IconeCouronne taille={14} /> Signer et Sceller</div>
                    {!memoHolding && (
                      <div style={{ background: '#FEF9E7', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#9A7D0A', marginBottom: 12 }}>
                        <IconeAlerte taille={13} /> Le mémo Holding n'est pas encore dans ce dossier
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 12, lineHeight: 1.5 }}>
                      Votre signature enregistrée sera apposée sur le mémo Holding. Vous
                      pourrez <strong>vérifier l'aperçu</strong> avant de confirmer.
                    </div>
                    <textarea style={s.textarea}
                      placeholder="Commentaire optionnel..."
                      value={commentaire} onChange={e => setCommentaire(e.target.value)} />
                    <button onClick={chargerSignatureScellee} disabled={!memoHolding || loadingPreview}
                      style={{ ...s.btnSigner, ...(!memoHolding || loadingPreview ? { background: '#E5E7EB', color: '#aaa', cursor: 'not-allowed' } : {}) }}>
                      {loadingPreview ? <><IconeHorloge taille={14} /> Génération de l'aperçu...</> : <><IconePlume taille={14} /> Apposer ma signature</>}
                    </button>
                    <button onClick={() => setActionMode('rejeter')} style={s.btnRejeter}><IconeRefuse taille={13} /> Rejeter</button>
                  </div>
                )}

                {/* Confirmer scellage */}
                {previewBlobUrl && !actionMode && !scellageTermine && (
                  <div style={s.actionSection}>
                    <div style={{ background: '#EAFAF1', border: '1px solid #27AE60', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1E8449', marginBottom: 4 }}>
                        <IconeValide taille={13} /> Signature apposée — prévisualisation
                      </div>
                      <div style={{ fontSize: 11, color: '#555' }}>
                        Vérifiez le mémo Holding affiché à gauche, puis confirmez.
                      </div>
                    </div>
                    {infoSignataire && (
                      <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 10, marginBottom: 14, border: '0.5px solid #e5e7eb' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1B2A' }}>{infoSignataire.nom}</div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                          {infoSignataire.role} · {infoSignataire.date}
                        </div>
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 12, lineHeight: 1.5 }}>
                      <IconeAlerte taille={13} /> En confirmant, le dossier est scellé et archivé définitivement.
                    </div>
                    <button onClick={confirmerSignaturePDG} disabled={saving}
                      style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #C0392B, #96281B)', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8, boxShadow: '0 4px 15px rgba(192,57,43,0.4)' }}>
                      {saving ? <><IconeHorloge taille={14} /> Scellage en cours...</> : <><IconeCouronne taille={14} /> Signer et Sceller définitivement</>}
                    </button>
                    <button onClick={reinitialiserPreview} style={s.btnRejeter}>
                      <IconeRafraichir taille={13} /> Annuler
                    </button>
                  </div>
                )}

                {/* Rejet */}
                {actionMode === 'rejeter' && (
                  <div style={s.actionSection}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#96281B', marginBottom: 10 }}><IconeRefuse taille={13} /> Motif du rejet</div>
                    <textarea style={s.textarea} placeholder="Motif obligatoire..." value={commentaire} onChange={e => setCommentaire(e.target.value)} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => { setActionMode(null); setCommentaire(''); }}
                        style={{ flex: 1, padding: '10px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Annuler
                      </button>
                      <button onClick={rejeter} disabled={saving}
                        style={{ flex: 2, padding: '10px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {saving ? <><IconeHorloge taille={13} />...</> : <><IconeRefuse taille={13} /> Confirmer</>}
                      </button>
                    </div>
                  </div>
                )}

                <button onClick={() => navigate('/demandes/' + (selected.id || selected._id))}
                  style={{ width: '100%', padding: '10px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#666', marginTop: 8 }}>
                  <IconeDossier taille={14} /> Voir le dossier complet
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, system-ui, sans-serif', background: '#F5F6FA', overflow: 'hidden' },
  topbar: { background: '#0D1B2A', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  btnNav: { padding: '6px 14px', borderRadius: 8, border: '0.5px solid rgba(255,255,255,0.2)', background: 'transparent', color: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
  main: { flex: 1, display: 'flex', overflow: 'hidden' },
  liste: { width: 280, background: '#fff', borderRight: '0.5px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  listeTitre: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '0.5px solid #e5e7eb', background: '#F9FAFB', flexShrink: 0 },
  listeItem: { padding: '14px 16px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer', background: '#fff' },
  emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center', color: '#888', fontSize: 13 },
  splitVide: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' },
  split: { flex: 1, display: 'flex', overflow: 'hidden' },
  splitLeft: { flex: 1, display: 'flex', flexDirection: 'column', background: '#2D3748', overflow: 'hidden', position: 'relative' },
  onglets: { display: 'flex', background: '#1A202C', borderBottom: '1px solid #4A5568', flexShrink: 0, overflowX: 'auto', alignItems: 'center' },
  onglet: { padding: '10px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer', border: 'none', background: 'transparent', color: '#A0AEC0', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  ongletActif: { background: '#2D3748', color: '#fff', borderBottom: '2px solid #C0392B' },
  pdfZone: { flex: 1, position: 'relative', overflow: 'hidden' },
  splitRight: { width: 360, display: 'flex', flexDirection: 'column', background: '#fff', borderLeft: '0.5px solid #e5e7eb', overflow: 'hidden' },
  decisionHeader: { background: 'linear-gradient(135deg, #0D1B2A, #1A3A5C)', padding: '18px 20px', flexShrink: 0 },
  decisionBody: { flex: 1, overflowY: 'auto', padding: 16 },
  resumeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 },
  resumeItem: { background: '#F9FAFB', borderRadius: 8, padding: '8px 12px', border: '0.5px solid #e5e7eb' },
  resumeLabel: { fontSize: 9, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 },
  resumeValue: { fontSize: 12, fontWeight: 600, color: '#0D1B2A' },
  actionSection: { marginBottom: 14 },
  actionTitle: { fontSize: 13, fontWeight: 700, color: '#0D1B2A', marginBottom: 10 },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', outline: 'none', background: '#F9FAFB', boxSizing: 'border-box', resize: 'none', height: 70, color: '#0D1B2A', marginBottom: 10 },
  btnSigner: { width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #C0392B, #96281B)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8, boxShadow: '0 3px 10px rgba(192,57,43,0.3)' },
  btnRejeter: { width: '100%', padding: '10px', borderRadius: 10, border: '1px solid #C0392B', background: 'transparent', color: '#C0392B', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 },
  successMsg: { background: '#EAFAF1', border: '0.5px solid #27AE60', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1E8449', fontWeight: 600, marginBottom: 12 },
  errorMsg: { background: '#FDEDEC', color: '#96281B', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 10 },
};
